import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiBarChart2, FiDollarSign, FiUsers } = FiIcons;

const CaseStudies = () => {
  const caseStudies = [
    {
      industry: "Healthcare Organization",
      challenge: "Multi-location medical practice struggling with HIPAA compliance, high call volumes, and escalating communication costs across 5 locations. Needed a system that could handle sensitive patient data while improving efficiency.",
      solution: "Implemented 3CX Enterprise system featuring secure call recording, advanced IVR for patient routing, and integrated appointment notification system. Added mobile apps for providers and custom call flows for each department.",
      results: [
        "47% reduction in monthly communication costs",
        "99.9% system uptime achieved",
        "Full HIPAA compliance with audit trails",
        "90% faster patient call routing"
      ],
      metrics: {
        savings: "56,000",
        satisfaction: "98%",
        efficiency: "35%"
      }
    },
    {
      industry: "Educational Institution",
      challenge: "Large K-12 school facing outdated infrastructure, rising maintenance costs, and poor communication between departments. Required emergency broadcast capabilities and better parent engagement tools.",
      solution: "Deployed 3CX Professional with emergency notification system, mobile apps for staff, and automated attendance calling. Integrated with existing PA system and implemented classroom-specific features.",
      results: [
        "38% reduction in annual phone expenses",
        "100% coverage for emergency broadcasts",
        "Seamless remote learning integration",
        "75% improvement in parent communication"
      ],
      metrics: {
        savings: "28,000",
        satisfaction: "96%",
        efficiency: "40%"
      }
    },
    {
      industry: "Municipal Government",
      challenge: "Local government office struggling with outdated phone system, lack of transparency in call handling, and inability to efficiently route constituent calls to appropriate departments.",
      solution: "Implemented 3CX Enterprise with department-specific routing, call recording for quality assurance, and emergency services integration. Added public service announcements and after-hours handling.",
      results: [
        "42% reduction in operating costs",
        "24/7 emergency service availability",
        "60% faster constituent response time",
        "Improved interdepartmental coordination"
      ],
      metrics: {
        savings: "45,000",
        satisfaction: "97%",
        efficiency: "45%"
      }
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="w-4/5 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Industry Success Stories
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how organizations across different sectors benefit from switching to 3CX
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {caseStudies.map((study, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="p-8">
                <div className="text-primary-600 font-semibold mb-4">
                  {study.industry}
                </div>
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Challenge:
                  </h4>
                  <p className="text-gray-600">{study.challenge}</p>
                </div>
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Solution:
                  </h4>
                  <p className="text-gray-600">{study.solution}</p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 mb-6">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-xl sm:text-2xl font-bold text-primary-600">
                      ${study.metrics.savings}
                    </div>
                    <div className="text-sm text-gray-600">Annual Savings</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-xl sm:text-2xl font-bold text-primary-600">
                      {study.metrics.satisfaction}
                    </div>
                    <div className="text-sm text-gray-600">Satisfaction</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-xl sm:text-2xl font-bold text-primary-600">
                      {study.metrics.efficiency}
                    </div>
                    <div className="text-sm text-gray-600">More Efficient</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Key Results:
                  </h4>
                  <ul className="space-y-2">
                    {study.results.map((result, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <SafeIcon
                          icon={FiArrowRight}
                          className="h-4 w-4 text-primary-600 mr-2"
                        />
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;